import React, { useEffect, useRef, useState } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

/**
 * Cleans Quill-generated HTML for backend/email usage
 * (does NOT touch the editor DOM)
 */
const cleanQuillHTML = (html) => {
  if (!html) return '';

  const div = document.createElement('div');
  div.innerHTML = html;

  // Remove Quill helper spans
  div.querySelectorAll('.ql-ui').forEach(el => el.remove());

  // Remove Quill-only attributes
  div.querySelectorAll('[data-list]').forEach(el => {
    el.removeAttribute('data-list');
  });

  div.querySelectorAll('[contenteditable]').forEach(el => {
    el.removeAttribute('contenteditable');
  });

  return div.innerHTML.trim();
};

const QuillEditor = ({
  value = '',
  onChange,
  placeholder = 'Enter text...',
  className = '',

  readOnly = false
}) => {
  const editorRef = useRef(null);
  const quillRef = useRef(null);
  const isInternalChange = useRef(false);
  const [charCount, setCharCount] = useState(0);

  /**
   * Initialize Quill (runs once)
   */
  useEffect(() => {
    if (!editorRef.current || quillRef.current) return;

    const quill = new Quill(editorRef.current, {
      theme: 'snow',
      placeholder,
      readOnly,
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['link'],
          ['clean']
        ]
      }
    });

    quillRef.current = quill;

    // Set initial value
    if (value) {
      quill.clipboard.dangerouslyPasteHTML(value, 'silent');
      setCharCount(quill.getText().trim().length);
    }

    /**
     * Handle user typing
     */
    quill.on('text-change', (delta, oldDelta, source) => {
      if (source !== 'user') return;

      const text = quill.getText().trim();
      const length = text.length;

      setCharCount(length);

      const rawHtml = quill.root.innerHTML;
      const cleanedHtml = cleanQuillHTML(rawHtml);

      isInternalChange.current = true;
      onChange?.(cleanedHtml);
    });

  }, []);

  /**
   * Sync external value → Quill (cursor-safe)
   */
  useEffect(() => {
    if (!quillRef.current) return;

    // Skip updates triggered by user typing
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    const quill = quillRef.current;
    const currentHTML = quill.root.innerHTML;

    // Normalize both values for comparison (trim and handle empty cases)
    const normalizedValue = (value || '').trim();
    const normalizedCurrent = currentHTML.trim();

    // Check if they're actually different (ignore minor HTML formatting differences)
    const isDifferent = normalizedValue !== normalizedCurrent &&
      normalizedValue !== '<p><br></p>' && // Quill's empty state
      !(normalizedValue === '' && normalizedCurrent === '<p><br></p>');

    if (isDifferent) {
      const selection = quill.getSelection();
      quill.clipboard.dangerouslyPasteHTML(value || '', 'silent');
      if (selection) {
        quill.setSelection(selection);
      }
    }

    setCharCount(quill.getText().trim().length);
  }, [value]);

  return (
    <div className={`quill-editor-wrapper ${className}`}>
      <div
        ref={editorRef}
        className="quill-editor"
        style={{ maxHeight: '300px', minHeight: '300px', overflowY: 'auto' }}
      />


      <style jsx>{`
        .quill-editor-wrapper {
          position: relative;
        }
        .quill-editor {
          background: white;
          border-radius: 0.375rem;
        }
        :global(.ql-toolbar) {
          border-top-left-radius: 0.375rem;
          border-top-right-radius: 0.375rem;
          border: 1px solid #e5e7eb;
          background: #f9fafb;
        }
        :global(.ql-container) {
          border-bottom-left-radius: 0.375rem;
          border-bottom-right-radius: 0.375rem;
          border: 1px solid #e5e7eb;
          border-top: none;
          font-family: inherit;
        }
        :global(.ql-editor) {
          min-height: 150px;
          font-size: 0.875rem;
        }
        :global(.ql-editor.ql-blank::before) {
          color: #9ca3af;
          font-style: normal;
        }
      `}</style>
    </div>
  );
};

export default QuillEditor;
